import React, { useEffect } from "react";

const SITE_URL = "https://lazydayshome.com";
const BRAND = "Lazy Days Home Services";
// Google SERP limits (Screaming Frog uses the same pixel widths).
const TITLE = { chars: 60, px: 561, font: "20px Arial" };
const DESC = { chars: 155, px: 985, font: "14px Arial" };

let ctx;
const width = (text, font) => {
  if (!ctx) ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return text.length * 9;
  ctx.font = font;
  return ctx.measureText(text).width;
};
const fits = (text, lim) => text.length <= lim.chars && width(text, lim.font) <= lim.px;

const clip = (text, lim) => {
  if (fits(text, lim)) return text;
  const words = text.split(" ");
  while (words.length > 1 && !fits(words.join(" "), lim)) words.pop();
  return words.join(" ").replace(/[\s|—–\-,:;&]+$/, "");
};

const clipDesc = (text) => {
  const cut = clip(text, DESC);
  if (cut === text) return cut;
  const end = cut.lastIndexOf(". ");
  return end > 100 ? cut.slice(0, end + 1) : cut;
};

// Fits titles in Google's snippet: full brand, short brand, or no brand.
export const buildTitle = (title) => {
  if (!title) return BRAND;
  const base = title.replace(/\s*[|—–-]\s*Lazy Days( Home Services)?\s*$/i, "").trim();
  if (/lazy days/i.test(base)) return clip(base, TITLE);
  const options = [`${base} | ${BRAND}`, `${base} | Lazy Days`, base];
  return options.find(t => fits(t, TITLE)) || clip(base, TITLE);
};

export function SEOHead({ title, description, keywords, structuredData, canonicalPath, noindex }) {
  useEffect(() => {
    const fullTitle = buildTitle(title);
    if (title) document.title = fullTitle;
    const desc = description ? clipDesc(description) : "";
    // robots: noindex pages (e.g. blogs/ZIP pages while SEO is paused); default index.
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
    robots.setAttribute("content", noindex ? "noindex, nofollow" : "index, follow");
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && desc) metaDesc.setAttribute("content", desc);
    else if (desc) { const m = document.createElement("meta"); m.name = "description"; m.content = desc; document.head.appendChild(m); }
    const metaKey = document.querySelector('meta[name="keywords"]');
    if (metaKey && keywords) metaKey.setAttribute("content", keywords);
    else if (keywords) { const m = document.createElement("meta"); m.name = "keywords"; m.content = keywords; document.head.appendChild(m); }
    if (structuredData) {
      let el = document.getElementById("structured-data-ld");
      if (!el) { el = document.createElement("script"); el.id = "structured-data-ld"; el.type = "application/ld+json"; document.head.appendChild(el); }
      el.textContent = JSON.stringify(structuredData);
    }
    // Canonical URL — use explicit path or derive from current location
    const path = canonicalPath !== undefined ? canonicalPath : window.location.pathname;
    const canonicalUrl = `${SITE_URL}${path === "/" ? "" : path}${path === "/" ? "/" : ""}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonicalUrl;
    // Per-page Open Graph + Twitter card tags
    const socialTags = [
      ["property", "og:title", fullTitle], ["property", "og:description", desc], ["property", "og:url", canonicalUrl],
      ["name", "twitter:title", fullTitle], ["name", "twitter:description", desc], ["name", "twitter:url", canonicalUrl],
    ];
    socialTags.forEach(([attr, key, val]) => {
      if (!val) return;
      let meta = document.querySelector(`meta[${attr}="${key}"]`);
      if (!meta) { meta = document.createElement("meta"); meta.setAttribute(attr, key); document.head.appendChild(meta); }
      meta.setAttribute("content", val);
    });
  }, [title, description, keywords, structuredData, canonicalPath, noindex]);
  return null;
}

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Lazy Days Home Services",
  "image": "https://lazydayshome.com/images/logo.png",
  "url": "https://lazydayshome.com",
  "telephone": "(817) 277-7387",
  "email": "Info@LazyDaysHome.com",
  "address": { "@type": "PostalAddress", "addressLocality": "Arlington", "addressRegion": "TX", "addressCountry": "US" },
  "geo": { "@type": "GeoCoordinates", "latitude": 32.7357, "longitude": -97.1081 },
  "priceRange": "$$",
  "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "17:00" },
  "areaServed": [
    { "@type": "City", "name": "Arlington, TX" },
    { "@type": "City", "name": "Fort Worth, TX" },
    { "@type": "City", "name": "Grand Prairie, TX" },
    { "@type": "City", "name": "Kennedale, TX" },
    { "@type": "City", "name": "Mansfield, TX" }
  ],
  "serviceType": ["House Cleaning","Deep Cleaning","Move In/Out Cleaning","Office Cleaning","Maid Service"],
  "sameAs": ["https://www.facebook.com/lazydayshome"]
};
